/**
 * 《사흘》 v7 — 백설리.
 *
 * **좌표를 「도보 분」에서 가져왔다.** 지도(v7 시절 03_맵 부록 — 지금은 이 파일이 정본)가
 * 정한 시간이 먼저고 미터가 나중이다 — 반대로 하면 걸어 보고 나서 「시간이 안 맞네」가 된다.
 *
 *     1분 = 12m        느린 눈길 걸음. 조합센터→한서진 집 4분 = 48m
 *
 * 그 값들이 정사에서 역산된 것이라 바꾸면 알리바이가 깨진다.
 *
 *     송이경  조합센터 21:35 → 21:38   3분
 *     명주원  조합센터 22:05 → 22:10   5분
 *     배정호  재무실   23:20 → 23:25   5분
 *     윤가람  별동     22:42 → 22:49   7분
 *
 * **한서진 집만 위에 있다.** 윤가람이 내려오다 배정호가 올라오는 것을 보는
 * 동선(E32)이 이 경사에서 나온다. 평지면 그 목격이 성립할 이유가 없다.
 *
 * 기존 `level-sahol.ts`(v3)는 손대지 않는다. 4,300줄을 직접 수술하면 깨졌을 때
 * 원인을 못 찾는다. 가구 조각(`pieces.ts`)과 조립기(`kernel.ts`)는 그대로 쓴다 —
 * 버리는 것은 좌표뿐이다.
 */
import type { BuildingDef, RoomDef, WallDef } from './level-defs';
import { WALL_H, WALL_T } from './level-defs';
import type { LevelDef, RampDef } from './level-types';

export { WALL_H, WALL_T };

/** 1분을 걷는 거리. 눈길이라 느리다 */
export const M_PER_MIN = 12;

/** 한서진 집이 앉은 단의 높이. 오르막 48m 를 3.6m 올라간다 — 7.5% 경사 */
export const HILL_Y = 3.6;

/* ------------------------------------------------------------------ */
/* 방 — 조합센터 안이 잘게 나뉘어야 한다.                                 */
/*                                                                     */
/* v5 배치표가 조합센터에 카드 18장을 몰아 뒀다. 한 칸이면 그 방 하나를    */
/* 뒤지고 게임이 끝난다. 1층 여섯 · 별동 다섯으로 가른다.                  */
/* ------------------------------------------------------------------ */

const L = '#e2e5e4'; // 형광등
const W = '#ffe6c0'; // 백열
const C = '#f2c79a'; // 로스터리

export const ROOMS: RoomDef[] = [
  /* --- 조합센터 1층. 구 백설분교 본관 ---
   *
   * **평면은 내가 짜는 것이 아니라 정사에 있다** — v7 시절 03_맵 §4 의 1층 그림:
   *
   *     현관 / 신발장 / 공용 키 캐비닛
   *     공용사무실 | 회의실   | 회계·재무실
   *     카페·로스터리 | 공동주방 | 창고·자료실
   *     복도 / 화장실 / 비상발전기실
   *
   * 구현은 위 세 띠만 있고 **맨 아래 서비스 띠가 통째로 빠져 있었다.** 그래서 방끼리 직접
   * 뚫린 격자가 됐다. 한 번 중복도형으로 새로 짰다가 되돌렸다 — 방 순서까지 바뀌어 정사와
   * 어긋났다(2026-09-11, Chris: 「스토리랑 전체 이야기를 보고 만들어야」).
   *
   * 크기도 고쳤다. 28×32=896㎡ 에 방 일곱이면 방 하나가 96~168㎡ 다 — 실제 시골 조합
   * 사무실(40~45)·회의실(30~35)·재무실(18~22)의 **3~5배**라 무엇을 넣어도 체육관이었다.
   * 18×22=396㎡ 로 줄여 방을 17~63㎡ 에 넣는다. 정사 §1 이 「3D 에서는 실제 이동시간을
   * 그대로 구현하지 않고 압축한다」고 해 두어 건물을 줄여도 도보 시간 설정과 어긋나지 않는다. */
  { id: 'cc_hall', name: '조합센터 현관', x1: -9, z1: 0.5, x2: 9, z2: 4, light: L, fixture: 'fluorescent', floor: 'tile' },
  { id: 'cc_office', name: '공용사무실', x1: -9, z1: -7, x2: -3, z2: 0.5, light: L, fixture: 'fluorescent' },
  { id: 'cc_meet', name: '회의실', x1: -3, z1: -7, x2: 3, z2: 0.5, light: L, fixture: 'fluorescent' },
  { id: 'bj_fin', name: '재무실', x1: 3, z1: -7, x2: 9, z2: 0.5, light: L, fixture: 'fluorescent' },
  /* **로스팅실은 카페와 다른 방이다** (2026-09-11, Chris: 「로스팅룸은 따로 보통 만들지」).
   * 원작에도 따로 나온다 — 한서진이 「로스팅실로 내려와서」 말하고, 02:11 에 「유리야 로스팅실
   * 온도 좀 봐줘」를 보낸다. 온도를 따로 챙기는 방이면 손님 자리와 같은 칸일 수 없다.
   * 실제로도 열·연기·채프 먼지·소음 때문에 생산 구역을 유리나 벽으로 가른다.
   * 정사 §4 의 한 칸(카페·로스터리)을 둘로 쪼갠다 — 칸 자체는 그대로 두고 안에서 나눈 것이다.
   * 로스팅실은 **복도 쪽**에 둔다: 생두는 뒷복도로 들여오고 손님은 카페에서 멈춘다. */
  { id: 'cy_cafe', name: '카페', x1: -9, z1: -10.5, x2: -3, z2: -7, light: C, fixture: 'fluorescent', floor: 'concrete' },
  /* 3.2m 깊이로는 로스터(0.9)와 포장대(0.7)가 마주 보고 못 선다 — 솔버가 「옆구리가 벽에」로 거절.
   * 4m 로 넓히고 카페를 3.5m 로 줄인다. 실제 소규모 로스팅실이 15~20㎡ 다 */
  { id: 'cy_roast', name: '로스팅실', x1: -9, z1: -14.5, x2: -3, z2: -10.5, light: C, fixture: 'fluorescent', floor: 'concrete' },
  { id: 'cc_kitchen', name: '공동주방', x1: -3, z1: -14.5, x2: 3, z2: -7, light: W, fixture: 'fluorescent', floor: 'tile' },
  { id: 'cc_arch', name: '기록보관실', x1: 3, z1: -14.5, x2: 9, z2: -7, light: L, fixture: 'fluorescent' },
  /* 정사 §4 의 맨 아래 띠 — 여태 없던 것. 복도가 뒷문(언덕 쪽)까지 이어진다 */
  { id: 'cc_corr', name: '조합센터 뒷복도', x1: -9, z1: -18, x2: 4, z2: -14.5, light: L, fixture: 'fluorescent', floor: 'tile' },
  { id: 'cc_gen', name: '비상발전기실', x1: 4, z1: -18, x2: 9, z2: -14.5, light: L, fixture: 'fluorescent', floor: 'concrete' },

  /* --- 별동. 본관에서 남쪽으로 36m(3분) 떨어져 있다 --- */
  { id: 'mj_shop', name: '목공·시설 작업실', x1: -16, z1: 30, x2: -4, z2: 44, light: '#d6c8b0', fixture: 'fluorescent', floor: 'concrete' },
  { id: 'yg_studio', name: '도예 작업실', x1: -4, z1: 30, x2: 8, z2: 44, light: '#e6d9c2', fixture: 'fluorescent', floor: 'concrete' },
  { id: 'yg_kiln', name: '가마실', x1: 8, z1: 30, x2: 16, z2: 38, light: '#f0c090', fixture: 'fluorescent', floor: 'concrete' },
  /* 사진실을 둘로 가른다 — **인화는 암실에서 한다**(Chris 2026-09-10: 「사진 인화하는데 창이 있으면 이상하다」).
   * 창은 남벽 x=12 와 동벽 z=41 뿐이라, 북동 구석(x 12.4~16 · z 38~40.4)은 창이 안 닿는다. 거기를 암실로 막는다 */
  /* **암실을 먼저 적는다.** `roomAt` 은 첫 일치를 돌려주는데, 암실이 사진실 사각형 안에 있어서
   * 뒤에 두면 그 안에 서 있어도 「사진·기록실」로 잡히고 붉은 등이 안 켜졌다(2026-09-10) */
  { id: 'si_dark', name: '암실', x1: 12.4, z1: 38, x2: 16, z2: 40.4, light: '#c4322a', fixture: 'pendant' },   // 안전등 — 붉은 전구
  { id: 'si_photo', name: '사진·기록실', x1: 8, z1: 38, x2: 16, z2: 44, light: '#dfe0d8', fixture: 'fluorescent' },

  /* --- 구 관사. 조합센터 동쪽, 환상로 건너 ---
   *
   * 예능 루틴에 필요한 방이 마을에 없었다: 브리핑은 회의실(cc_meet)이 하지만,
   * **탐정이 1:1 로 신문할 닫힌 방**과 **부검·포렌식 자료가 도착하는 자리**는
   * 조합원 공간이어선 안 된다(용의자가 제 방에서 신문받는 꼴이 된다). 1970년대
   * 표준설계 분교에는 교사 관사가 딸려 있었고, 조합은 그걸 창고로 쓰다가 정우빈이
   * 도착한 밤 비웠다. 4륜 경찰차가 환상로에 서 있고 그 옆이 이 집이다.
   */
  { id: 'cc_inv', name: '관사 임시수사실', x1: 28, z1: 0, x2: 34, z2: 10, light: L, fixture: 'fluorescent' },
  { id: 'cc_intv', name: '관사 신문실', x1: 34, z1: 0, x2: 38, z2: 10, light: W, fixture: 'fluorescent' },

  /* --- 한서진 집. 오르막 위 단 --- */
  /**
   * **한서진 집은 네 칸이다** (Chris 2026-09-07: 「집이 벽도 안 세워 두고 하지 않잖아」).
   * 거실(현관 포함)·업무공간·침실·다용도실. 침실 문은 닫혀 있고 「마감 중」 표식이 붙어 있다 —
   * 사흘 동안 아무도 안 연 이유(HS01)가 곧 방 구조다. 방 id `hs_room` 은 거실이 이어받는다.
   */
  { id: 'hs_room', name: '한서진의 집 거실', x1: -8, z1: -58, x2: 1, z2: -52, y: HILL_Y, light: '#c8ced6', wall: 'paper_plain:grey', wains: null, floor: 'plank:ash', dress: { kind: 'blind', color: '#d9dbdd', open: 0.25 }, fixture: 'fluorescent' },
  { id: 'hs_work', name: '한서진의 집 업무공간', x1: 1, z1: -60, x2: 6, z2: -52, y: HILL_Y, light: '#c8ced6', wall: 'paint:grey', wains: null, floor: 'carpet:grey', dress: { kind: 'blind', color: '#d9dbdd', open: 0.25 }, fixture: 'fluorescent' },
  { id: 'hs_bed', name: '한서진의 집 침실', x1: -8, z1: -66, x2: 1, z2: -58, y: HILL_Y, light: '#c4ccd6', wall: 'paper_plain:grey', wains: null, floor: 'plank:ash', dress: { kind: 'blind', color: '#d9dbdd', open: 0.25 }, fixture: 'pendant' },
  { id: 'hs_util', name: '한서진의 집 다용도실', x1: 1, z1: -63.5, x2: 6, z2: -60, y: HILL_Y, light: '#cfd3cc', wall: 'paint:white', wains: null, floor: 'lino:grey', dress: { kind: 'blind', color: '#d9dbdd', open: 0.25 }, fixture: 'fluorescent' },
  { id: 'hs_bath', name: '한서진의 집 화장실', x1: 1, z1: -66, x2: 3, z2: -63.5, y: HILL_Y, light: '#d4d8dc', wall: 'paint:white', wains: 'wains_tile:white', floor: 'ftile_check:bw', dress: { kind: 'blind', color: '#d9dbdd', open: 0.1 }, fixture: 'fluorescent' },
  { id: 'hs_store', name: '한서진의 집 창고', x1: 3, z1: -66, x2: 6, z2: -63.5, y: HILL_Y, light: '#cfd3cc', wall: 'paint:white', wains: null, floor: 'lino:grey', fixture: 'fluorescent' },

  /* --- 주택 다섯. 조합센터에서 각각 3~7분 --- */
  /* 송이경의 집 — 현관 남(z=−6). 칸막이: z=−10.5(현관 띠|안쪽) · x=−36(거실|주방) · x=−37(침실|다용도). 창 N −40·−36, W/E −16·−12 사이를 지난다 */
  { id: 'si_room', name: '송이경의 집 거실', x1: -44, z1: -10.5, x2: -36, z2: -6, light: '#cfc6b8', wall: 'paint:dark', wains: 'wains_board:dark', floor: 'plank:dark', dress: { kind: 'curtain', color: '#3c3742', open: 0.35 }, fixture: 'fluorescent' },
  { id: 'si_kit', name: '송이경의 집 주방', x1: -36, z1: -10.5, x2: -32, z2: -6, light: '#cfc6b8', wall: 'paint:dark', wains: 'wains_tile:black', floor: 'ftile_check:bw', dress: { kind: 'curtain', color: '#3c3742', open: 0.35 }, fixture: 'fluorescent' },
  { id: 'si_bed', name: '송이경의 집 침실', x1: -44, z1: -18, x2: -37, z2: -10.5, light: '#c8bfb0', wall: 'paint:dark', wains: null, floor: 'plank:dark', dress: { kind: 'curtain', color: '#3c3742', open: 0.35 }, fixture: 'fluorescent' },
  { id: 'si_util', name: '송이경의 집 다용도실', x1: -37, z1: -15, x2: -32, z2: -10.5, light: '#c6c0b4', wall: 'paint:grey', wains: null, floor: 'cement:grey', dress: { kind: 'curtain', color: '#3c3742', open: 0.35 }, fixture: 'fluorescent' },
  { id: 'si_bath', name: '송이경의 집 화장실', x1: -37, z1: -18, x2: -35, z2: -15, light: '#d8d4cc', wall: 'paint:white', wains: 'wains_tile:white', floor: 'ftile_check:bw', dress: { kind: 'blind', color: '#e8e8e4', open: 0.1 }, fixture: 'fluorescent' },
  { id: 'si_store', name: '송이경의 집 창고', x1: -35, z1: -18, x2: -32, z2: -15, light: '#c6c0b4', wall: 'paint:grey', wains: null, floor: 'cement:grey', fixture: 'fluorescent' },
  /* 윤가람의 집 — 현관 남(z=−6), 송이경 집의 거울상. 칸막이 z=−10.5 · x=36 · x=37 */
  { id: 'yg_room', name: '윤가람의 집 거실', x1: 36, z1: -10.5, x2: 44, z2: -6, light: '#e9e6df', wall: 'plaster_rough:white', wains: null, floor: 'fstone:warm', dress: { kind: 'curtain', color: '#f3f1ec', open: 0.75 }, fixture: 'fluorescent' },
  { id: 'yg_kit', name: '윤가람의 집 주방', x1: 32, z1: -10.5, x2: 36, z2: -6, light: '#e9e6df', wall: 'plaster_rough:white', wains: 'wains_tile:white', floor: 'fstone:warm', dress: { kind: 'curtain', color: '#f3f1ec', open: 0.75 }, fixture: 'fluorescent' },
  { id: 'yg_bed', name: '윤가람의 집 침실', x1: 37, z1: -18, x2: 44, z2: -10.5, light: '#ece8e0', wall: 'plaster_rough:white', wains: null, floor: 'plank:ash', dress: { kind: 'curtain', color: '#f3f1ec', open: 0.75 }, fixture: 'fluorescent' },
  { id: 'yg_util', name: '윤가람의 집 다용도실', x1: 32, z1: -15, x2: 37, z2: -10.5, light: '#e0dcd2', wall: 'plaster_rough:white', wains: null, floor: 'cement:grey', dress: { kind: 'curtain', color: '#f3f1ec', open: 0.75 }, fixture: 'fluorescent' },
  { id: 'yg_bath', name: '윤가람의 집 화장실', x1: 32, z1: -18, x2: 34, z2: -15, light: '#ece8e0', wall: 'paint:white', wains: 'wains_tile:white', floor: 'ftile_check:bw', dress: { kind: 'blind', color: '#f0f0ec', open: 0.1 }, fixture: 'fluorescent' },
  { id: 'yg_store', name: '윤가람의 집 창고', x1: 34, z1: -18, x2: 37, z2: -15, light: '#e0dcd2', wall: 'plaster_rough:white', wains: null, floor: 'cement:grey', fixture: 'fluorescent' },
  /* 명주원의 집 — 현관 북(z=18). 칸막이 z=23.5(창 22·26 사이) · x=−49(거실|주방) · x=−52(침실|다용도, 창 −55·−49 사이) */
  { id: 'mj_room', name: '명주원의 집 거실', x1: -58, z1: 18, x2: -49, z2: 23.5, light: '#e8d4b0', wall: 'panel_board:pine', wains: 'wains_board:walnut', floor: 'plank_wide:pine', dress: { kind: 'curtain', color: '#cdbf9e', open: 0.8 }, fixture: 'fluorescent' },
  { id: 'mj_kit', name: '명주원의 집 주방', x1: -49, z1: 18, x2: -46, z2: 23.5, light: '#e8d4b0', wall: 'paint:white', wains: 'wains_tile:white', floor: 'lino:grey', dress: { kind: 'curtain', color: '#cdbf9e', open: 0.8 }, fixture: 'fluorescent' },
  { id: 'mj_bed', name: '명주원의 집 침실', x1: -58, z1: 23.5, x2: -52, z2: 30, light: '#e4d2b4', wall: 'panel_board:pine', wains: 'wains_board:walnut', floor: 'plank_wide:pine', dress: { kind: 'curtain', color: '#cdbf9e', open: 0.8 }, fixture: 'fluorescent' },
  { id: 'mj_util', name: '명주원의 집 다용도실', x1: -52, z1: 23.5, x2: -46, z2: 27.7, light: '#ddd0b8', wall: 'panel_board:pine', wains: null, floor: 'cement:grey', dress: { kind: 'curtain', color: '#cdbf9e', open: 0.8 }, fixture: 'fluorescent' },
  { id: 'mj_bath', name: '명주원의 집 화장실', x1: -52, z1: 27.7, x2: -50, z2: 30, light: '#e8e6e0', wall: 'paint:white', wains: 'wains_tile:white', floor: 'ftile_check:bw', fixture: 'fluorescent' },
  { id: 'mj_store', name: '명주원의 집 창고', x1: -50, z1: 27.7, x2: -46, z2: 30, light: '#ddd0b8', wall: 'panel_board:pine', wains: null, floor: 'cement:grey', fixture: 'fluorescent' },
  /* 차유리의 집 — 현관 북(z=18), 명주원 집의 거울상. 칸막이 z=23.5 · x=55 · x=52 */
  { id: 'cy_room', name: '차유리의 집 거실', x1: 46, z1: 18, x2: 55, z2: 23.5, light: '#f0e2c8', wall: 'paint:white', wains: null, floor: 'parquet:oak', dress: { kind: 'curtain', color: '#efe6d2', open: 0.65 }, fixture: 'fluorescent' },
  { id: 'cy_kit', name: '차유리의 집 주방', x1: 55, z1: 18, x2: 58, z2: 23.5, light: '#f0e2c8', wall: 'paint:white', wains: 'wains_tile:white', floor: 'ftile_check:bw', dress: { kind: 'curtain', color: '#efe6d2', open: 0.65 }, fixture: 'fluorescent' },
  { id: 'cy_bed', name: '차유리의 집 침실', x1: 46, z1: 23.5, x2: 52, z2: 30, light: '#f0dcc4', wall: 'paint:peach', wains: null, floor: 'carpet:grey', dress: { kind: 'curtain', color: '#efe6d2', open: 0.65 }, fixture: 'fluorescent' },
  { id: 'cy_util', name: '차유리의 집 다용도실', x1: 52, z1: 23.5, x2: 58, z2: 27.7, light: '#e6e0d0', wall: 'paint:white', wains: null, floor: 'lino:grey', dress: { kind: 'curtain', color: '#efe6d2', open: 0.65 }, fixture: 'fluorescent' },
  { id: 'cy_bath', name: '차유리의 집 화장실', x1: 56, z1: 27.7, x2: 58, z2: 30, light: '#f0ece4', wall: 'paint:white', wains: 'wains_tile:white', floor: 'ftile_check:bw', fixture: 'fluorescent' },
  { id: 'cy_store', name: '차유리의 집 창고', x1: 52, z1: 27.7, x2: 56, z2: 30, light: '#e6e0d0', wall: 'paint:white', wains: null, floor: 'lino:grey', fixture: 'fluorescent' },
  /* 배정호의 집 — 현관 서(x=88, z=−2). 칸막이 x=93(창 91·97 사이) · z=1(거실|주방) · z=−2(침실|다용도, 동창 −5·1 사이) */
  { id: 'bj_room', name: '배정호의 집 거실', x1: 88, z1: -8, x2: 93, z2: 1, light: '#d8dce0', wall: 'paper_stripe:grey', wains: 'wains_paint:grey', floor: 'lino:grey', dress: { kind: 'blind', color: '#f0f0ee', open: 0.5 }, fixture: 'fluorescent' },
  { id: 'bj_kit', name: '배정호의 집 주방', x1: 88, z1: 1, x2: 93, z2: 4, light: '#d8dce0', wall: 'paint:white', wains: 'wains_tile:white', floor: 'ftile_check:bw', dress: { kind: 'blind', color: '#f0f0ee', open: 0.5 }, fixture: 'fluorescent' },
  { id: 'bj_bed', name: '배정호의 집 침실', x1: 93, z1: -8, x2: 100, z2: -2, light: '#d4d8de', wall: 'paper_stripe:grey', wains: 'wains_paint:grey', floor: 'carpet:grey', dress: { kind: 'blind', color: '#f0f0ee', open: 0.5 }, fixture: 'fluorescent' },
  { id: 'bj_util', name: '배정호의 집 다용도실', x1: 93, z1: -2, x2: 98, z2: 4, light: '#d0d4d8', wall: 'paint:grey', wains: null, floor: 'cement:grey', dress: { kind: 'blind', color: '#f0f0ee', open: 0.5 }, fixture: 'fluorescent' },
  { id: 'bj_bath', name: '배정호의 집 화장실', x1: 98, z1: -2, x2: 100, z2: 0, light: '#dcdee2', wall: 'paint:white', wains: 'wains_tile:white', floor: 'ftile_check:bw', fixture: 'fluorescent' },
  { id: 'bj_store', name: '배정호의 집 창고', x1: 98, z1: 0, x2: 100, z2: 4, light: '#d0d4d8', wall: 'paint:grey', wains: null, floor: 'cement:grey', fixture: 'fluorescent' },
];

/* ------------------------------------------------------------------ */
/* 벽                                                                  */
/* ------------------------------------------------------------------ */

// **문은 폭 1.0 · 높이 2.2.** 반폭 0.95(폭 1.9m)는 문이 아니라 통로였다 — 가로로 넓고 세로로 짧아 보인 이유
// (Chris 2026-09-10). 문짝 조각 `door_panel` 의 폭 1.0 과 맞춘다
const DOOR_HALF = 0.5;
const DOOR_HEAD = 2.2;
const WIN_SILL = 0.95;
const WIN_HEAD = 2.15;
const WIN_HALF = 0.85;

/**
 * 벽에 뚫은 창 구멍 목록. **`wallRun` 이 뚫으면서 직접 채운다.**
 *
 * 손으로 따로 적으면 반드시 어긋난다 — 실제로 v7 은 이 배열이 빈 채였고,
 * 그 결과 뚫린 구멍에 틀도 유리도 안 들어가 조합센터 정면이 「붉은 띠 두 줄과
 * 그 사이 검은 줄」로 나왔다. 구멍을 내는 쪽이 목록도 만들게 한다.
 *
 * `y` 는 창턱의 절대 높이다. 언덕 위 집은 바닥이 3.6m 올라가 있어서
 * 이것이 없으면 창만 땅바닥에 남는다.
 */
export const WINDOWS: {
  x: number; z: number; along: 'x' | 'z'; open?: boolean; half?: number; y?: number;
  /** 낳은 wallRun 키와 그 run 의 창 목록 안 순번 — 배치 에디터가 되짚어 고친다 */
  run?: string; i?: number;
}[] = [];

/**
 * **벽 한 줄의 정의.** `wallRun`/`box` 는 이제 이 목록에 자기를 적고 나서 벽을 뱉는다.
 * 그래서 배치 에디터가 창·문을 옮기면(`rebuildWalls`) 코드를 안 고치고 같은 정의에서 벽을 다시 짓는다.
 * 키 = `축@고정좌표:시작..끝`. 코드의 좌표를 바꾸면 키도 바뀌므로 에디터 오버라이드는 확정 즉시 코드로 옮긴다.
 */
export interface WallRunDef {
  key: string; along: 'x' | 'z'; at: number; from: number; to: number;
  doors: number[]; windows: number[]; baseY: number;
}
export const WALL_RUNS: WallRunDef[] = [];
/** 에디터 오버라이드 — run 키별로 문 중심 목록과 창(중심·반폭) 목록을 통째로 바꾼다 */
/** 문은 중심만 적던 것을 폭도 담게 넓혔다 — 에디터에서 문 폭을 바꾸려면 필요하다(2026-09-11).
 *  숫자만 있는 옛 저장도 그대로 읽는다(그때는 DOOR_HALF 를 쓴다). */
export type DoorOv = number | { c: number; half?: number; h?: number };
export type OpeningOverride = { doors?: DoorOv[]; windows?: { c: number; half?: number }[] };

/**
 * 문 개구부 목록. **`wallRun` 이 뚫으면서 직접 채운다** — 창과 같은 이유다.
 *
 * v7 은 이 배열이 빈 채로 나갔고, 그래서 마을 전체에 문틀이 **하나도** 없었다.
 * 조합센터 뒷문도 집 현관도 그냥 벽에 뚫린 사각 구멍이었다.
 * `y` 는 문지방의 절대 높이 — 언덕 위 집은 3.6m 올라가 있다.
 */
export const DOORWAYS: { x: number; z: number; along: 'x' | 'z'; y?: number; half?: number; run?: string; i?: number }[] = [];

/** 벽 한 줄. 문 자리는 인방만 남기고, 창 자리는 허리벽 위로 뚫는다 */
function wallRun(
  along: 'x' | 'z',
  at: number,
  from: number,
  to: number,
  opts: { doors?: number[]; windows?: number[]; baseY?: number } = {},
): WallDef[] {
  const run: WallRunDef = { key: `${along}@${at}:${from}..${to}`, along, at, from, to, doors: opts.doors ?? [], windows: opts.windows ?? [], baseY: opts.baseY ?? 0 };
  WALL_RUNS.push(run);
  return emitRun(run);
}

/** 정의(+오버라이드) 하나에서 벽 토막·창·문을 뱉는다 */
function emitRun(run: WallRunDef, ov?: OpeningOverride): WallDef[] {
  const { along, at, from, to } = run;
  // 오르막 위 집은 바닥이 올라가 있다. 문·창 높이도 통째로 따라 올라가야
  // 문지방이 땅에 박히지 않는다.
  const B = run.baseY;
  const doorsRaw: DoorOv[] = ov?.doors ?? run.doors;
  // 문마다 높이가 다를 수 있다(2026-09-11) — 안 적힌 문은 전역 DOOR_HEAD 를 쓴다
  const doors = doorsRaw.map((d) => (typeof d === 'number' ? { c: d, half: DOOR_HALF, h: DOOR_HEAD } : { c: d.c, half: d.half ?? DOOR_HALF, h: d.h ?? DOOR_HEAD }));
  const wins: { c: number; half?: number }[] = ov?.windows ?? run.windows.map((c) => ({ c }));
  const cuts = [
    ...doors.map((d, i) => ({ c: d.c, half: d.half, door: true, i, h: d.h })),
    ...wins.map((w, i) => ({ c: w.c, half: w.half ?? WIN_HALF, door: false, i })),
  ].sort((a, b) => a.c - b.c);

  const out: WallDef[] = [];
  const seg = (a: number, b: number, y?: number, h?: number, gap?: 'door' | 'window') => {
    if (b - a < 0.02) return;
    // y 를 안 준 통벽은 baseY 부터 WALL_H 만큼. B 가 0 이면 undefined 로 남겨
    // 기존 벽처럼 걸레받이·몰딩이 붙는다 (world.ts 가 `!w.y` 로 판단한다)
    const yy = y === undefined ? (B || undefined) : B + y;
    const hh = y === undefined ? (B ? WALL_H : undefined) : h;
    out.push(along === 'x'
      ? { x1: a, z1: at, x2: b, z2: at, y: yy, h: hh, gap, base: B || undefined, run: run.key }
      : { x1: at, z1: a, x2: at, z2: b, y: yy, h: hh, gap, base: B || undefined, run: run.key });
  };
  let cur = from;
  for (const k of cuts) {
    seg(cur, k.c - k.half);
    if (k.door) {
      const dh = (k as { h?: number }).h ?? DOOR_HEAD;   // 문마다 높이가 다를 수 있다
      seg(k.c - k.half, k.c + k.half, dh, WALL_H - dh, 'door');
      // 개구부 반폭을 같이 보낸다 — 문틀(world.ts door_casing)이 이 값으로 폭을 잡는다.
      // 안 보내면 문틀이 옛 1.9 로 남는다(Chris 2026-09-10: 「틀이 그 전 사이즈가 남아있는 것 같지 않아?」)
      DOORWAYS.push(
        along === 'x'
          ? { x: k.c, z: at, along: 'x', y: B || undefined, half: k.half, run: run.key, i: k.i }
          : { x: at, z: k.c, along: 'z', y: B || undefined, half: k.half, run: run.key, i: k.i },
      );
    } else {
      seg(k.c - k.half, k.c + k.half, 0, WIN_SILL, 'window');
      seg(k.c - k.half, k.c + k.half, WIN_HEAD, WALL_H - WIN_HEAD, 'window');
      WINDOWS.push(
        along === 'x'
          ? { x: k.c, z: at, along: 'x', half: k.half, y: B + WIN_SILL, run: run.key, i: k.i }
          : { x: at, z: k.c, along: 'z', half: k.half, y: B + WIN_SILL, run: run.key, i: k.i },
      );
    }
    cur = k.c + k.half;
  }
  seg(cur, to);
  return out;
}

/** 사각형 한 채를 두른다 */
function box(
  x1: number,
  z1: number,
  x2: number,
  z2: number,
  o: { doorsN?: number[]; doorsS?: number[]; doorsW?: number[]; doorsE?: number[];
       winN?: number[]; winS?: number[]; winW?: number[]; winE?: number[];
       baseY?: number } = {},
): WallDef[] {
  const b = o.baseY;
  return [
    ...wallRun('x', z1, x1, x2, { doors: o.doorsN, windows: o.winN, baseY: b }),
    ...wallRun('x', z2, x1, x2, { doors: o.doorsS, windows: o.winS, baseY: b }),
    ...wallRun('z', x1, z1, z2, { doors: o.doorsW, windows: o.winW, baseY: b }),
    ...wallRun('z', x2, z1, z2, { doors: o.doorsE, windows: o.winE, baseY: b }),
  ];
}

export const WALLS: WallDef[] = [
  /* 조합센터 본관 외곽 18×22. 현관은 남쪽(z=4) 한가운데, 뒷문은 북쪽(z=−18) 복도 끝 */
  ...box(-9, -18, 9, 4, {
    doorsS: [0],
    // **뒷문.** 언덕이 건물 북쪽이라 없으면 한서진 집에 가려고 건물을 통째로 돈다.
    // 정사 §4 의 서비스 띠가 생기면서 주방이 아니라 **복도**가 뒷문을 갖는다
    doorsN: [0],
    // 외장은 창 자리를 비워 두고 붙으므로 안 뚫으면 맨 구조벽 띠가 남는다.
    // 현관(x=0, 반폭 0.5)을 비켜 좌우 대칭
    winS: [-5, 5],
    /* **방이 작아지면 개구부부터 줄여야 한다.** 5.7m 벽에 문 둘·창 둘을 뚫었더니 붙일 벽이
     * 3m 도 안 남아 솔버가 19개를 「문 앞 구역」·「창 앞」으로 거절했다(2026-09-11).
     * 방마다 **바깥벽 창 하나**로 줄인다. 가운데 열(회의실·주방)은 바깥벽이 없어 창이 없다 —
     * 3열 평면의 당연한 결과고 실제 건물도 그렇다. 창 반폭 0.85 라 칸막이에서 0.85 이상 띄운다 */
    winN: [-5, 6.5],
    winW: [-3.25, -8.8, -12.5, -16.25],   // 사무실 · 카페 · 로스팅실(환기) · 복도
    winE: [-3.25, -10.75, -16.25],
  }),
  /* 본관 내부 — 정사 §4 의 네 띠. 가로 칸막이 셋에만 문을 낸다 */
  // 현관 | 1열(사무실·회의실·재무실)
  ...wallRun('x', 0.5, -9, 9, { doors: [-6, 0, 6] }),
  // 1열 | 2열(카페·주방·기록실) — 현관에서 뒷복도로 가는 유일한 길이라 문 셋을 다 둔다
  ...wallRun('x', -7, -9, 9, { doors: [-6, 0, 6] }),
  // 2열 | 서비스 띠
  ...wallRun('x', -14.5, -9, 9, { doors: [-6, 0, 6] }),
  // 세로 칸막이는 **문 없이** 막는다. 옆방끼리 문까지 내면 5.7m 벽에 개구부가 셋이 된다
  ...wallRun('z', -3, -14.5, 0.5),
  // 카페 | 로스팅실 — 손님은 여기서 멈춘다
  ...wallRun('x', -10.5, -9, -3, { doors: [-6] }),
  ...wallRun('z', 3, -14.5, 0.5),
  // 복도 | 비상발전기실
  ...wallRun('z', 4, -18, -14.5, { doors: [-16.2] }),

  /* 별동. 현관은 북쪽(z=30) */
  // **32m 짜리 벽에 창이 하나였다.** 작업동은 목공·도예·가마·암실이 줄줄이 들어앉은
  // 건물인데 정면이 통째로 맨 함석이라 창고가 아니라 컨테이너로 보였다. 방마다 창을 낸다.
  ...box(-16, 30, 16, 44, {
    // 가마는 장작·기물을 들여야 해서 반입문이 따로 있다(x=12)
    doorsN: [-10, 2, 12],
    winN: [-14, -6, -1, 5, 10, 14],
    winS: [-12, -6, 0, 6, 12],
    winW: [36], winE: [34, 41],
  }),
  ...wallRun('z', -4, 30, 44, { doors: [37] }),           // 목공실 | 도예실
  ...wallRun('z', 8, 30, 44, { doors: [34, 41] }),        // 도예실 | 가마·사진실
  ...wallRun('x', 38, 8, 16, { doors: [12] }),            // 가마실 | 사진실
  /* 암실 칸막이 — 창이 없는 북동 구석. 문은 서쪽 벽 z=39.4 (편집실에서 들어간다) */
  ...wallRun('z', 12.4, 38, 40.4, { doors: [39.4] }),
  ...wallRun('x', 40.4, 12.4, 16),

  /* 한서진 집. 오르막 위. 현관은 남쪽(z=-52) */
  // **남쪽이 이 집의 얼굴이다** — 언덕을 올라오는 카메라가 정면으로 보는 면인데
  // 문 하나만 있고 창이 없어서 3.6m 짜리 맨 벽으로 나왔다. 창을 뚫어야
  // 「불 켜진 집」이 된다 (sashes 의 bay 해시상 -5 는 켜짐, 2.5 는 흐릿하게 켜짐)
  ...box(-8, -66, 6, -52, {
    baseY: HILL_Y, doorsS: [-1], winS: [-5, 2.5],
    // 서벽 창은 −59.5: −62 에 두면 붙박이장(폭 2.0, z −64.2~−62.2)이 창을 덮는다
    winN: [-4, 2], winW: [-59.5, -56], winE: [-62, -56],
  }),
  /* 한서진 집 칸막이 — 거실|업무(x=1, 문 z=−55) · 거실|침실(z=−58, 문 x=−3.5) · 침실|다용도(x=1, 문 z=−63) · 업무|다용도(z=−60, 문 x=3.5) */
  ...wallRun('z', 1, -66, -52, { doors: [-63, -55], baseY: HILL_Y }),
  ...wallRun('x', -58, -8, 1, { doors: [-3.5], baseY: HILL_Y }),
  ...wallRun('x', -60, 1, 6, { doors: [3.5], baseY: HILL_Y }),
  ...wallRun('x', -63.5, 1, 6, { doors: [2, 4.5], baseY: HILL_Y }),   // 다용도|화장실·창고
  ...wallRun('z', 3, -66, -63.5, { baseY: HILL_Y }),

  /* 언덕 축대. 위로 올라가는 길은 비탈 하나뿐이라는 걸 **지형이** 강제한다 —
   * 규칙으로 적어 두는 대신 못 올라가게 막는다. 남쪽 x -6~6 만 트여 있다 */
  ...[
    { x1: -24, z1: -48, x2: -6, z2: -48 },
    { x1: 6, z1: -48, x2: 24, z2: -48 },
    { x1: -24, z1: -74, x2: 24, z2: -74 },
    { x1: -24, z1: -74, x2: -24, z2: -48 },
    { x1: 24, z1: -74, x2: 24, z2: -48 },
  ].map((w) => ({ ...w, y: 0, h: HILL_Y })),

  /* 주택 다섯 */
  ...box(-44, -18, -32, -6, { doorsS: [-38], winN: [-40, -36], winW: [-16, -12], winE: [-16, -12] }),
  ...box(32, -18, 44, -6, { doorsS: [38], winN: [35, 41], winW: [-16, -12], winE: [-16, -12] }),
  ...box(-58, 18, -46, 30, { doorsN: [-52], winS: [-55, -49], winW: [22, 26], winE: [22, 26] }),
  ...box(46, 18, 58, 30, { doorsN: [52], winS: [49, 55], winW: [22, 26], winE: [22, 26] }),
  ...box(88, -8, 100, 4, { doorsW: [-2], winN: [91, 97], winS: [91, 97], winE: [-5, 1] }),
  /* 주택 다섯 칸막이 (2026-09-10, Chris: 「경계선 나누어서 만들면 되지 않나」). 한서진 집과 같은 wallRun.
   * 축 'x' = z 고정선이 x 구간을 달림, 'z' = x 고정선이 z 구간을 달림. 문은 두 방의 공통 구간 안에 */
  // 송이경
  ...wallRun('x', -10.5, -44, -32, { doors: [-40, -34] }),   // 현관 띠|안쪽: 거실→침실 −40 · 주방→다용도 −34
  ...wallRun('z', -36, -10.5, -6, { doors: [-8] }),          // 거실|주방
  ...wallRun('z', -37, -18, -10.5, { doors: [-14] }),        // 침실|다용도
  ...wallRun('x', -15, -37, -32, { doors: [-36, -33.5] }),   // 다용도|화장실·창고
  ...wallRun('z', -35, -18, -15),
  // 윤가람
  ...wallRun('x', -10.5, 32, 44, { doors: [40, 34] }),
  ...wallRun('z', 36, -10.5, -6, { doors: [-8] }),
  ...wallRun('z', 37, -18, -10.5, { doors: [-14] }),
  ...wallRun('x', -15, 32, 37, { doors: [33, 35.5] }),
  ...wallRun('z', 34, -18, -15),
  // 명주원
  ...wallRun('x', 23.5, -58, -46, { doors: [-55, -47.5] }),
  ...wallRun('x', 27.7, -52, -46, { doors: [-51, -48] }),   // 다용도|화장실·창고 (2026-09-15 화장실)
  ...wallRun('z', -50, 27.7, 30),                              // 화장실|창고
  ...wallRun('z', -49, 18, 23.5, { doors: [20] }),
  ...wallRun('z', -52, 23.5, 30, { doors: [27] }),
  // 차유리
  ...wallRun('x', 23.5, 46, 58, { doors: [49, 56.5] }),
  ...wallRun('x', 27.7, 52, 58, { doors: [57, 54] }),
  ...wallRun('z', 56, 27.7, 30),
  ...wallRun('z', 55, 18, 23.5, { doors: [20] }),
  ...wallRun('z', 52, 23.5, 30, { doors: [27] }),
  // 배정호
  ...wallRun('z', 93, -8, 4, { doors: [-5, 2.5] }),          // 현관 띠|안쪽: 거실→침실 −5 · 주방→다용도 2.5
  ...wallRun('x', 1, 88, 93, { doors: [90.5] }),             // 거실|주방
  ...wallRun('z', 98, -2, 4, { doors: [-1, 2.5] }),           // 다용도|화장실·창고
  ...wallRun('x', 0, 98, 100),
  ...wallRun('x', -2, 93, 100, { doors: [96.5] }),           // 침실|다용도
  /* 관사. 현관은 서쪽(환상로 쪽) z=5. 신문실은 창을 남쪽 하나만 — 닫힌 방이어야 한다.
   * **z 0 부터다.** −2 에 세웠더니 윤가람 옆길(z −5.6~−2.4)의 눈 둑이 북벽을 뚫고
   * 신문실 바닥에 흰 쐐기로 들어왔다 — 길 둑은 길보다 0.8m 넓다. */
  ...box(28, 0, 38, 10, { doorsW: [5], winN: [31], winS: [31, 36], winE: [5] }),
  ...wallRun('z', 34, 0, 10, { doors: [3] }),               // 수사실 | 신문실
];

/* ------------------------------------------------------------------ */
/* 건물 껍데기와 문패                                                    */
/* ------------------------------------------------------------------ */

/**
 * **벽을 다시 짓는다** — 배치 에디터 오버라이드(run 키별 문·창)로. `WALLS`·`WINDOWS`·`DOORWAYS` 를 제자리에서
 * 비우고 다시 채우므로 이미 import 한 쪽(world.ts·architecture.ts)도 같은 배열을 본다. wallRun 이 낳지 않은
 * 벽(언덕 옹벽 등)은 그대로 둔다. 씬을 짓기 **전에** 불러야 한다.
 */
export function rebuildWalls(ov: Record<string, OpeningOverride> = {}): void {
  const keep = WALLS.filter((w) => !w.run);
  WALLS.length = 0; WINDOWS.length = 0; DOORWAYS.length = 0;
  for (const run of WALL_RUNS) WALLS.push(...emitRun(run, ov[run.key]));
  WALLS.push(...keep);
}

export const BUILDINGS: BuildingDef[] = [
  { id: 'center', x1: -9, z1: -18, x2: 9, z2: 4, archetype: 'school',
    // 폭이 28 → 18m. 같은 15° 면 용마루가 9×tan15° = 2.4m 로 납작해지므로 20° 로 세운다
    // (용마루 3.3m). 교문 10m 앞에서 올려다보는 각에 지붕 윗면이 걸려야 띠가 안 된다
    style: { roof: 'slate', pitchDeg: 20, stack: 'brickR', wall: 'block' },
    entrances: [{ x: 0, z: 4, along: 'x', label: '백설분교' }] },
  /**
   * **별동은 두 채다.**
   *
   * 한 덩어리 32 x 14m 상자로 세웠더니 함석 골을 아무리 손봐도 컨테이너였다.
   * 실제 시골 작업동은 본채를 먼저 짓고 가마실을 나중에 덧붙인다 — 용마루 방향이
   * 꺾이고, 지붕 높이가 어긋나고, 벽 재료가 바뀐다. 그 셋이 실루엣을 만든다.
   *
   *  본채(목공·도예)  x -16..8  : 24m 폭 → 용마루 x 축, 물매 23°, 마루 6.5m, 함석 벽
   *  가마실 wing      x  8..16  :  8m 폭 → 용마루 **z 축**, 물매 17°, 마루 4.8m, 블록 벽
   *
   * 마루가 1.7m 낮고 방향이 직각이라 멀리서도 두 채로 읽힌다. 연통은 가마가 있는
   * 쪽으로 옮긴다(본채 stack 을 none 으로 내린다) — 굴뚝이 한 채에 하나여야
   * 「가마가 저기 있다」가 읽힌다.
   */
  { id: 'annex', x1: -16, z1: 30, x2: 8, z2: 44, archetype: 'shed',
    style: { roof: 'tinGreen', pitchDeg: 23, stack: 'none', wall: 'panel' },
    entrances: [{ x: -10, z: 30, along: 'x', label: '목공실' }, { x: 2, z: 30, along: 'x', label: '도예실' }] },
  { id: 'annex_kiln', x1: 8, z1: 30, x2: 16, z2: 44, archetype: 'shed',
    // 불을 때는 칸이라 벽이 콘크리트 블록이다. 지붕도 본채 녹색 함석이 아니라 붉게 삭은 함석
    style: { roof: 'tinRed', pitchDeg: 17, stack: 'flue', wall: 'block' },
    entrances: [{ x: 12, z: 30, along: 'x', label: '가마실' }] },
  { id: 'gwansa', x1: 28, z1: 0, x2: 38, z2: 10, archetype: 'house',
    // 회벽 관사. 학교와 같은 시절 건물이라 지붕도 학교와 같은 슬레이트
    style: { roof: 'slate', pitchDeg: 24, stack: 'flue', wall: 'render' },
    entrances: [{ x: 28, z: 5, along: 'z', label: '관사' }] },
  { id: 'han', x1: -8, z1: -66, x2: 6, z2: -52, archetype: 'house', baseY: HILL_Y,
    // 언덕 위 — 마을 어디서나 능선 위로 보이는 유일한 집이다. 물매를 제일 세우고
    // 벽을 미장 회벽으로 밝혀 하늘을 배경으로 실루엣이 끊기게 한다
    style: { roof: 'slate', pitchDeg: 30, stack: 'brickR', wall: 'render', porch: true },
    entrances: [{ x: -1, z: -52, along: 'x', label: '한서진' }] },
  { id: 'song', x1: -44, z1: -18, x2: -32, z2: -6, archetype: 'house',
    style: { roof: 'tinGreen', pitchDeg: 22, stack: 'flue', wall: 'block' },
    entrances: [{ x: -38, z: -6, along: 'x', label: '송이경' }] },
  { id: 'yun', x1: 32, z1: -18, x2: 44, z2: -6, archetype: 'house',
    style: { roof: 'tile', pitchDeg: 34, stack: 'brickL', wall: 'block', porch: true },
    entrances: [{ x: 38, z: -6, along: 'x', label: '윤가람' }] },
  { id: 'myeong', x1: -58, z1: 18, x2: -46, z2: 30, archetype: 'house',
    style: { roof: 'tinBlue', pitchDeg: 24, stack: 'flue', wall: 'panel' },
    entrances: [{ x: -52, z: 18, along: 'x', label: '명주원' }] },
  { id: 'yuri', x1: 46, z1: 18, x2: 58, z2: 30, archetype: 'house',
    style: { roof: 'tinRed', pitchDeg: 28, stack: 'brickL', wall: 'render', porch: true },
    entrances: [{ x: 52, z: 18, along: 'x', label: '차유리' }] },
  { id: 'bae', x1: 88, z1: -8, x2: 100, z2: 4, archetype: 'house',
    // 마을에서 제일 낮은 물매 + 굴뚝 없음. 외길 끝의 이 집만 윤곽이 납작하다
    style: { roof: 'slate', pitchDeg: 19, stack: 'none', wall: 'panel', lit: 'dark' },
    entrances: [{ x: 88, z: -2, along: 'z', label: '배정호' }] },
];

/* ------------------------------------------------------------------ */
/* 지형 — 오르막 하나. 이 경사가 E32 목격의 근거다.                       */
/* ------------------------------------------------------------------ */

/**
 * 조합센터 **뒷문**(z=-28) 바로 밖에서 시작해 18m 를 3.6m 올라간다 — 20% 경사.
 *
 * 처음에는 z=-6 에서 시작하게 썼는데, 그러면 비탈이 조합센터(z -28~4) 를
 * 관통해 회의실·공동주방 한가운데를 뚫고 지나간다. 평면도를 그려 보고서야 봤다.
 *
 * **한 갈래다.** 갈라지면 「올라가는 걸 봤다」가 「어디로 갔는지 모른다」가 된다.
 * 폭 10m 로 좁게 두는 것도 같은 이유다 — 오르막 위 사람은 반드시 마주친다.
 */
export const RAMPS: RampDef[] = [
  { id: 'hill', yLow: 0, yHigh: HILL_Y, x1: -4.5, x2: 4.5, lowZ: -30, highZ: -48, outdoor: true, buried: true },
];

export const SAHOLV7_LEVEL: LevelDef = {
  id: 'saholv7',
  name: '사흘 · 백설리',
  floors: [
    {
      id: 'ground', name: '백설리', y: 0, height: WALL_H,
      slabs: [
        [-70, -80, 112, 60],
        // 언덕 위 평지. 비탈 꼭대기(z=-48)에서 이어져 한서진 집을 얹는다
        [-24, -74, 24, -48, HILL_Y],
      ],
      walls: [], zones: [], pieces: [], lamps: [],
    },
  ],
  ramps: RAMPS,
  bounds: { x1: -70, z1: -80, x2: 112, z2: 60 },
  slabT: 0.3,
  wallT: WALL_T,
};

/** 좌표가 어느 방인지. 밖이면 null */
export function roomAt(x: number, z: number): RoomDef | null {
  for (const r of ROOMS) {
    if (x >= Math.min(r.x1, r.x2) && x <= Math.max(r.x1, r.x2) &&
        z >= Math.min(r.z1, r.z2) && z <= Math.max(r.z1, r.z2)) return r;
  }
  return null;
}

/**
 * 1차 시작 자리. 조합센터 **교문 안쪽**에 여섯이 선다.
 *
 * 처음엔 현관 바로 앞(z 8~11)이었는데, 포치가 현관에서 2.6m 튀어나와
 * 스폰과 1.4m 밖에 안 떨어진다 — 첫 화면이 창 하나 없는 포치 측벽
 * 콘크리트로 꽉 차서 「이 벽 뭐여」가 됐다(2026-09-17 Chris). 교문
 * 안쪽(z 12.5~14)으로 물리면 포치+창 띠+『백설분교』문패가 한 화면에
 * 들어와 학교로 읽힌다. 조회대·게양대·골대가 앞뒤로 겹쳐 운동장도 읽는다.
 */
export function spawnPoint(index: number): [number, number, number] {
  const spots: [number, number][] = [[-4, 12.5], [-2, 13.5], [0, 12.5], [2, 13.5], [4, 12.5], [0, 14.5]];
  const [x, z] = spots[index % spots.length];
  return [x, 0, z];
}

/* ------------------------------------------------------------------ */
/* 길 — 눈 쌓인 평면 위에 다져진 자국.                                    */
/*                                                                     */
/* 마을 바닥은 통짜 한 장이라, 길이 없으면 어디로 가야 할지가 안 읽힌다.   */
/* 실제로 조감도에서 「어디가 길이냐」는 말이 먼저 나왔다.                  */
/*                                                                     */
/* 건물 발자국을 피해서 깐다. 처음 깔았을 때 대로가 z=28 이라 명주원·차유리 */
/* 집(z 18~30)을 관통했다 — 평면도에 겹쳐 그려 보고서야 봤다.             */
/* ------------------------------------------------------------------ */

import type { LampDef, OutdoorDef, RoadDef, TreeDef } from './outdoor';

/**
 * **`berm` 을 길마다 켰다.** 평지 길이 안 보이던 이유는 색이 아니라 깊이였다 —
 * 눈 20cm 위에 회색 띠를 그리면 그건 눈밭에 칠한 색띠지 길이 아니다. 치운 눈을
 * 양옆에 쌓아야 「파인 골」이 되고, 그때 비로소 눈이 몇 cm 인지가 화면에 나온다.
 * 언덕길(비탈)만 예외다 — 거기는 둑길 어깨가 이미 같은 일을 하고 있어서 둘을
 * 겹쳤더니 함석지붕처럼 골이 졌다.
 */
export const ROADS: RoadDef[] = [
  // 대로 — 마을을 동서로 관통한다. z 4~18 이 건물 없는 유일한 띠다
  { x1: -60, z1: 14.4, x2: 76, z2: 17.6, ruts: true, berm: true },

  // 환상로 — 조합센터를 좌우로 돌아 뒤편(언덕 밑)까지 간다.
  // 앞문(z=4)에서 뒷문(z=-28)으로 가려면 건물을 돌아야 한다
  { x1: -25.6, z1: -30, x2: -22.4, z2: 14.4, ruts: true, berm: true },
  { x1: 22.4, z1: -30, x2: 25.6, z2: 14.4, ruts: true, berm: true },
  // 뒤편 가로로 — 환상로 양끝을 잇고 언덕 초입에 닿는다
  // **z −28.2 까지다.** −27.6 까지 뒀더니 북벽(z=−28)을 0.4m 파고들어 공동주방
  // 싱크 뒤 바닥에 다진 눈 띠가 깔렸다 — 관사 때와 같은 사고(길이 벽을 뚫는다)
  { x1: -25.6, z1: -30.6, x2: 25.6, z2: -28.2, ruts: true, berm: true },

  // 송이경 · 윤가람 — 집 남쪽 면(z=-6)을 스치는 옆길
  { x1: -38, z1: -5.6, x2: -22.4, z2: -2.4, berm: true },
  { x1: 22.4, z1: -5.6, x2: 38, z2: -2.4, berm: true },

  // 명주원 · 차유리 — 대로에서 현관(z=18)까지 몇 걸음
  { x1: -53.4, z1: 14.4, x2: -50.6, z2: 18 },
  { x1: 50.6, z1: 14.4, x2: 53.4, z2: 18 },

  // 별동 두 현관 — 대로에서 남쪽으로 12m
  { x1: -11.4, z1: 17.6, x2: -8.6, z2: 30, berm: true },
  { x1: 0.6, z1: 17.6, x2: 3.4, z2: 30, berm: true },

  // 교문 — 여기서 길이 끊긴다. 현관까지는 운동장을 가로질러 간다
  { x1: -3, z1: 12, x2: 3, z2: 14.4 },
  // **교문에서 현관까지 한 줄 제설.** 운동장을 그냥 가로지르게 두면 정면 컷의
  // 아래 절반이 아무 정보 없는 흰 판이 되고, 눈이 20cm 쌓였다는 사실도 안 보인다.
  // 삽으로 낸 폭(1.6m)에 양옆으로 치운 눈을 쌓는다 — 사람이 여기 살고 있다는
  // 표시이자, 카메라를 현관으로 끌고 가는 선이다
  { x1: -0.8, z1: 4, x2: 0.8, z2: 12, berm: true },

  // 배정호 외길 — 대로 동쪽 끝에서 꺾어 혼자 떨어진 집으로.
  // 「제일 멀다」가 길의 길이로 보여야 한다
  { x1: 74.4, z1: -3.4, x2: 77.6, z2: 14.4, ruts: true, berm: true },
  { x1: 77.6, z1: -3.4, x2: 88, z2: -0.6, ruts: true, berm: true },

  // 언덕길 — **하나뿐이다.** 바퀴가 못 올라가는 경사라 발로만 다져져 있다.
  //
  // `berm` 이 없으면 이 길은 흰 비탈에 그린 연회색 띠일 뿐이고, 밤 조명에서
  // 그 띠는 아예 안 보인다 — 실제로 언덕길이 화면에서 사라졌다. 양옆에 치운
  // 눈이 40cm 둑으로 서면 길이 **색이 아니라 깊이**로 읽힌다: 파인 골 하나가
  // 언덕 밑에서 현관까지 이어진다
  // **6m 는 산골 오르막이 아니라 활주로였다.** 화면 절반을 회색 쐐기가 먹었다.
  // 경운기 한 대와 사람이 비켜 설 폭이면 된다 — 3.4m
  //
  // **둑을 뺐다.** 둑을 넣던 시점엔 언덕이 매끈한 쐐기였고 길을 나타낼 것이
  // 이것뿐이었다. 지금은 둑길(causeway) 자체가 어깨를 갖고 있어서, 둘이 겹치면
  // 길 양옆에 **나란한 능선이 두 겹**으로 선다. 01 컷이 함석지붕처럼 골이 진
  // 이유가 그거였다 — 어깨 + 둑 + 바퀴자국 띠가 전부 세로로 겹쳤다.
  // 같은 것(치운 눈)을 두 번 만들지 않는다. 어깨 쪽을 남긴다 — 지형 메쉬라
  // 눈 재질이 이어지고, 둑은 늘 겉도는 별개 띠였다.
  { x1: -1.7, z1: -30, x2: 1.7, z2: -48, y1: 0, y2: HILL_Y, ruts: true },
  // 언덕 위 — 한서진 집 현관까지. 골이 여기서 끊기면 안 된다.
  //
  // **둑(`berm`)을 뺐다.** 이 구간은 4m 뿐인데 둑 끝맺음이 양쪽 2.5m 를 먹는다
  // (`tap`). 그래서 4m 짜리 렌즈 모양 흰 더미 둘이 현관 앞에 남았고, 화면에서는
  // 마당에 **흰 종이 두 장을 오려 붙인 꼴**이었다 — 둑 코드 주석이 경고하는
  // 바로 그 실패다. 짧은 구간에 둑을 세우지 마라. 길은 여기서 색(다진 회색)과
  // 댓돌로 읽힌다.
  { x1: -2.5, z1: -52, x2: 0.5, z2: -48, y1: HILL_Y, y2: HILL_Y },
];

/* ------------------------------------------------------------------ */
/* 바깥 장식 — 나무·가로등·담·방풍림·운동장.                              */
/*                                                                     */
/* v3 값이 `client/src/outside.ts` 에 박혀 있어서, 마을을 새로 깔았는데도  */
/* 가로등 셋이 조합센터 **안에** 서 있었고 방풍림이 언덕 초입을 막았다.    */
/* ------------------------------------------------------------------ */

const TREES: TreeDef[] = [
  { x: -46, z: -22 }, { x: 46, z: -22 },
  { x: -62, z: 14 }, { x: 62, z: 14 },
  { x: -20, z: 20 }, { x: 20, z: 20 },
  { x: -30, z: 40 }, { x: 30, z: 40 },
  { x: 66, z: 6 }, { x: 80, z: -14 },
  // 오르막 양옆. 둑 발치가 위로 갈수록 벌어지므로 나무도 따라 벌어진다 —
  // **비교할 것이 하나도 없어서** 길이 활주로로 보였다. 사람 키의 몇 배인지를
  // 말해 주는 물건이 프레임 안에 있어야 한다
  { x: -6.8, z: -31 }, { x: 7.2, z: -33.5 },
  { x: -9.5, z: -38 }, { x: 16.5, z: -47 },   // 10.5,-40 도 13.2,-38.5 도 03 컷 시선축 위였다 — 화면을 통째로 막았다
  { x: -13, z: -45 }, { x: 13.5, z: -46 },
  // 언덕 위 — 밑동이 단 위에 앉는다. y 를 안 주면 공중에 뜬다.
  //
  // **언덕 단 위에 나무가 둘이었다.** 그래서 집이 「눈판 위에 놓인 상자」로
  // 읽혔다. 열두 그루를 집 둘레에 두르되, 여섯 컷의 시선축과는 3m 이상
  // 떨어뜨렸다(축을 막으면 화면이 통째로 나무가 된다 — 실제로 두 번 그랬다).
  // 집 자리는 x -8~6 / z -66~-52 라 그 사각형은 비운다.
  { x: 8, z: -56, y: HILL_Y }, { x: -10, z: -60, y: HILL_Y },
  // 서쪽 — 04·03 컷에서 집 뒤 실루엣이 된다
  { x: -19, z: -52, y: HILL_Y, h: 6.4 }, { x: -14.5, z: -57.5, y: HILL_Y, h: 5.2 },
  { x: -20, z: -61.5, y: HILL_Y, h: 7.0 }, { x: -16, z: -66.5, y: HILL_Y, h: 5.6 },
  { x: -21, z: -70.5, y: HILL_Y, h: 6.2 },
  // 북쪽 — 집 뒤. 05 컷에서는 등 뒤라 안 보이고 01·02 에서 지붕 위로 걸린다
  { x: -4, z: -71.5, y: HILL_Y, h: 6.8 }, { x: 3, z: -72.5, y: HILL_Y, h: 5.4 },
  { x: 11, z: -73, y: HILL_Y, h: 6.0 },
  // 동쪽 — 06 컷 오른쪽을 채운다
  { x: 15, z: -51.5, y: HILL_Y, h: 5.0 }, { x: 20, z: -55, y: HILL_Y, h: 6.6 },
  { x: 12.5, z: -58.5, y: HILL_Y, h: 5.8 }, { x: 18, z: -62, y: HILL_Y, h: 7.2 },
];

const LAMPS: LampDef[] = [
  // 대로
  // (0,13) 에 하나 있었다. 그 자리는 **교문 한복판**이라 정면 컷을 세로로 갈랐고,
  // 카메라가 설 자리이기도 했다. 교문 축을 비우고 좌우로 갈라 세운다
  { x: -52, z: 13 }, { x: -24, z: 13 }, { x: -10, z: 13 }, { x: 10, z: 13 }, { x: 24, z: 13 },
  { x: 52, z: 13 }, { x: 76, z: 13 },
  // 환상로
  // **관사 서쪽 현관이 나중에 z=5 로 생겼다.** (27.5,4) 은 그 문에서 0.5m 앞
  // 1m 옆이라, 등기둥이 처마 밑을 세로로 가르고 현관 컷을 반으로 잘랐다 —
  // 위 대로에서 교문 한복판 등을 치웠던 것과 같은 사고다. 길 어깨(환상로
  // 동쪽 끝 x=25.6)에 붙이고 문 축에서 남쪽으로 물린다. 문은 처마등이 맡는다
  { x: -27.5, z: 4 }, { x: -27.5, z: -14 }, { x: 26.0, z: 0.6 }, { x: 27.5, z: -14 },
  // 언덕 초입 — **한쪽이 나가 있다.** 오르막 입구가 어두운 이유가 이것이다
  { x: -9, z: -29 }, { x: 9, z: -29, dead: true },
  // 배정호 외길 — 긴 길에 두 개뿐이다
  // 끝의 하나는 **나가 있다.** 브리핑의 「가로등이 끊기고」가 이 자리다 —
  // 살려 뒀더니 46 짜리 등이 배정호 집 판벽을 통째로 띄워, 마을에서 제일
  // 어두워야 할 집이 제일 밝은 집이 됐다
  { x: 77.5, z: 4 }, { x: 84, z: -1, dead: true },
  // 언덕 위 — 오르막 끝의 하나. 브리핑이 말한 「빛 웅덩이」가 이것이다
  { x: 4, z: -50, y: HILL_Y },
  // 집 동쪽 마당. 이게 없으면 집을 옆에서 본 컷이 통째로 검게 나온다(실제로 그랬다).
  // 현관등 하나로는 14m 짜리 벽면과 지붕 물매를 못 띄운다
  { x: 10, z: -60, y: HILL_Y },
];

/**
 * **카메라가 서는 자리 — 여기엔 배경목을 심지 않는다.**
 *
 * 마을에 나무를 뿌리고 나서 대로 컷 하나가 통째로 날아갔다. 명주원 집 컷
 * (-44, 4)에서 침엽수 한 그루가 **카메라 앞 0.7m** 에 섰다. 화면의 8할이
 * 검은 잎이었고 집은 한 조각도 안 보였다. 탐침으로 재서야 원인을 알았다.
 *
 * 건물·길·현관을 피하는 것만으로는 안 된다. 마을에서 사람이 서는 자리는
 * 마당·공터이고 거기엔 원래 나무가 없다 — 그게 사람이 서는 이유다. 그러니
 * 그 자리를 좌표로 적는다. 값은 `docs/scenes/<id>/scene.json` 의 카메라
 * 위치와 같다(스킬: 「카메라 자리를 먼저 정하고 그 시선 축은 비운다」).
 *
 * 반경 9m 는 「코앞을 막지 않는」 최소치다. 20m 앞의 나무는 깊이를 만드니
 * 남긴다.
 */
export const CLEARINGS: { x: number; z: number; r: number }[] = [
  // 대로
  { x: -58, z: 16, r: 9 }, { x: -12, z: 17, r: 9 }, { x: 70, z: 16.5, r: 9 },
  { x: -38, z: 2, r: 9 }, { x: 46, z: 7, r: 9 }, { x: -44, z: 4, r: 9 },
  // 조합센터 정면
  { x: 0, z: 24, r: 9 }, { x: 0, z: 12, r: 9 }, { x: -34, z: 14, r: 9 },
  { x: 6, z: 10, r: 9 }, { x: -38, z: 20, r: 9 },
  // 배정호 외길
  { x: 76, z: 22, r: 9 }, { x: 74.8, z: 9.5, r: 9 }, { x: 76, z: -2, r: 9 },
  { x: 84, z: -2, r: 9 }, { x: 86, z: 2, r: 9 }, { x: 104, z: -14, r: 9 },
];

export const OUTDOOR: OutdoorDef = {
  trees: TREES,
  lamps: LAMPS,
  // 뒷밭 경계. 언덕 밑 좌우로 갈라 두어 「길은 가운데 하나뿐」을 눈으로 굳힌다
  fences: [
    { x1: -46, z1: -36, x2: -28, z2: -36 },
    { x1: 28, z1: -36, x2: 46, z2: -36 },
    { x1: -60, z1: 50, x2: -20, z2: 50 },
    { x1: 20, z1: 50, x2: 60, z2: 50 },
  ],
  groves: [
    { x1: -60, z1: -39, x2: -28, z2: -39, n: 12 },
    { x1: 28, z1: -39, x2: 60, z2: -39, n: 12 },
    { x1: -62, z1: 53, x2: -20, z2: 53, n: 14 },
    { x1: 20, z1: 53, x2: 62, z2: 53, n: 14 },
  ],
  // 폐교 백설분교의 운동장. 조합센터 현관(z=4) 앞 띠에 들어간다
  yard: {
    // 조회대·게양대를 운동장 한복판에 두면 교문–현관 시선 축을 막는다.
    // 둘을 서쪽으로 몰아 붙인다 — 실제 분교도 단상은 한쪽 끝에 있다
    podium: [-9, 7],
    flagpole: [-12.5, 7],
    goals: [[-11, 9, 'z'], [11, 9, 'z']],
    gate: [[-3.5, 14.4], [3.5, 14.4]],
  },
  // 언덕 한 덩이. 남쪽 x -6~6 만 트여 있고 나머지 셋은 가파르다.
  //
  // batter 5(35.8°) 는 **언덕이 아니라 축대**였다 — 달이 서쪽에 있어 남쪽으로
  // 선 급사면은 입사각이 0.44 밖에 안 되고, 눈도 덜 얹혀 판때기 한 장으로 섰다.
  // 8(24.2°) 로 눕히면 밑동이 z -40 까지 나와 비탈이 카메라 쪽으로 흘러내리고,
  // N.y 가 0.91 이라 눈이 거의 통째로 얹힌다.
  // 이게 없으면 단 밑이 뚫려서 언덕 아래에서 한서진 집 바닥 뒷면이 보인다
  /**
   * 별동 마당의 야적물. 문 셋(x -10 / 2 / 12) 사이 빈칸에만 놓아 드나드는
   * 동선을 막지 않는다. 가마 쪽(x 12~16)에 장작이 몰려 있어야 「저기가
   * 불 때는 칸」이 눈으로 읽힌다.
   */
  stacks: [
    { x: 7.0, z: 27.6, kind: 'firewood', rot: 0 },
    { x: 15.4, z: 27.8, kind: 'firewood', rot: 0 },
    { x: 18.2, z: 36.0, kind: 'firewood', rot: Math.PI / 2 },
    { x: -4.6, z: 28.2, kind: 'cylinders' },
    { x: -15.0, z: 28.0, kind: 'crates', rot: 0 },
    { x: -1.2, z: 27.8, kind: 'sacks', rot: 0 },
  ],
  mounds: [{ x1: -24, z1: -74, x2: 24, z2: -48, y: HILL_Y, batter: 8, gapS: [-6, 6] }],
  // gapS 로 뚫은 구멍을 메우는 흙둑. 경사로 정의(`hill`)와 같은 자리·같은 높이다
  causeways: [{ x1: -4.5, x2: 4.5, zLow: -30, zHigh: -48, yHigh: HILL_Y, batter: 2.6 }],
  // 뒷산 — 한서진 집(z -66~-52)을 삼키지 않게 더 뒤로 물렸다
  hills: [
    [-52, -84, 90, 26, 34],
    [44, -80, 70, 21, 30],
    [-34, 64, 80, 23, 32],
    [30, 62, 66, 19, 28],
    [120, 6, 60, 20, 30],
  ],
};
